import React, { useState, useEffect } from 'react'
import { Box, Typography, Button, CircularProgress } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useNavigate } from 'react-router-dom'

// Components
import { CustomModal } from '../../common/CustomModal'
import GeneralSection from './GeneralSection'
import EntrySection from '../editBill/EntrySection'
import NetTotalSection from './NetTotalSection'

// Others
import { calculateNetTotal, formatQuickBillMutationData, processEntries } from '../../../utils/billing'
import {
  EntryType,
  useFetchQuickBillDetailsLazyQuery,
  FetchQuickBillDetailsQuery,
  useGenerateQuickBillMutation,
  BillingType
} from '../../../../generated'
import { GRAPHQL_QUERY_POLICY } from '../../../constants'
import { Alert } from '../../common/Alert'
import { EditBillForm } from '../../../interfaceTypes/forms'
import { editBillEntryValidationSchema } from '../../../validationSchema'
import useFetchHybridCaseFlatFeeConsumed from '../../../hooks/useFetchHybridCaseFlatFeeConsumed'
import { formatCurrency } from '../../../utils'

interface QuickBillModalProps {
  isOpen: boolean
  onClose: () => void
  caseId: string
  refetch?: () => void
  navigateOnSuccess?: boolean
}

const QuickBillModal: React.FC<QuickBillModalProps> = ({ isOpen, onClose, caseId, refetch, navigateOnSuccess = false }) => {
  const navigate = useNavigate()
  const [data, setData] = useState<FetchQuickBillDetailsQuery['fetchQuickBillDetails']>()
  const [isFinalBill, setIsFinalBill] = useState(false)

  const { billingType, isFlatFeeHoursExhausted, flatFee } = data?.case?.billing || {}
  const { flatFeeConsumed } = useFetchHybridCaseFlatFeeConsumed({
    caseId: data?.case?.id || '',
    billingType: billingType || null,
    isFlatFeeHoursExhausted: !!isFlatFeeHoursExhausted
  })

  const methods = useForm<EditBillForm>({
    resolver: yupResolver(editBillEntryValidationSchema),
    mode: 'onChange',
    defaultValues: {
      timeEntries: [],
      legalFeeEntries: [],
      expenseEntries: [],
      investigatorEntries: [],
      discountPercentage: null
    }
  })

  const { handleSubmit, reset, watch } = methods
  const { netTotal, discount } = calculateNetTotal(watch())

  const [generateQuickBillMutation, { loading: generatingQuickBill }] = useGenerateQuickBillMutation({
    onError(error) {
      Alert.error(error.message || 'Failed to generate bill')
    },
    onCompleted(data) {
      if (data) {
        const {
          generateQuickBill: { response, billId }
        } = data

        if (response?.status === 200) {
          Alert.success('Bill generated successfully!')
          onClose()
          if (refetch) {
            refetch()
          }
          if (navigateOnSuccess) {
            navigate(`/billing/bill-detail/${billId}`)
          }
        } else {
          Alert.error('Failed to generate bill')
        }
      }
    }
  })

  const [fetchQuickBillDetails] = useFetchQuickBillDetailsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError(error) {
      Alert.error(error.message || 'Failed to fetch details')
    },
    onCompleted(data) {
      if (data?.fetchQuickBillDetails) {
        const { response } = data.fetchQuickBillDetails
        if (response?.status === 200) {
          const { case: litigation, entries } = data.fetchQuickBillDetails

          setData(data.fetchQuickBillDetails)

          const { timeEntries, legalFeeEntries, expenseEntries, investigatorEntries } = processEntries(entries)
          reset({
            timeEntries,
            legalFeeEntries,
            expenseEntries,
            investigatorEntries,
            discountPercentage: litigation.billing?.discountPercentage
          })
        }
      }
    }
  })

  const onSubmit: SubmitHandler<EditBillForm> = async data => {
    if (!caseId) return

    const formattedData = formatQuickBillMutationData({
      caseId,
      data,
      totalAmount: netTotal,
      discount
    })
    await generateQuickBillMutation({
      variables: {
        generateQuickBillInput: {
          ...formattedData,
          isFinalBill
        }
      }
    })
  }

  useEffect(() => {
    if (isOpen && caseId) {
      fetchQuickBillDetails({
        variables: {
          fetchQuickBillDetailsInput: {
            caseId
          }
        }
      })
    } else if (!isOpen) {
      setData(undefined)
      setIsFinalBill(false)
      reset({
        timeEntries: [],
        legalFeeEntries: [],
        expenseEntries: [],
        investigatorEntries: [],
        discountPercentage: null
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, caseId])

  return (
    <CustomModal title="Quick Bill" isOpen={isOpen} handleClose={onClose} maxWidth="xl" closeOnlyWithButton>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {billingType === BillingType.Hybrid && !isFlatFeeHoursExhausted && (
              <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2} mb={-2}>
                <Typography color="#333">
                  Client's current available flat fee to be used:{' '}
                  {formatCurrency(Math.max(0, (flatFee || 0) - flatFeeConsumed))}
                </Typography>
              </Box>
            )}

            <GeneralSection litigation={data?.case} />

            <EntrySection
              key="timeEntries"
              title="Time Entries"
              fieldName="timeEntries"
              entryType={EntryType.Time}
              caseId={data?.case?.id || ''}
              caseUsers={data?.case?.caseToUsers || []}
              billingType={data?.case?.billing?.billingType}
              isFlatFeeHoursExhausted={data?.case?.billing?.isFlatFeeHoursExhausted}
            />

            <EntrySection
              key="legalFeeEntries"
              title="Legal fee"
              fieldName="legalFeeEntries"
              entryType={EntryType.Legal}
              caseId={data?.case?.id || ''}
              caseUsers={data?.case?.caseToUsers || []}
              billingType={data?.case?.billing?.billingType}
              isFlatFeeHoursExhausted={data?.case?.billing?.isFlatFeeHoursExhausted}
            />

            <EntrySection
              key="expenseEntries"
              title="Expense Entries"
              fieldName="expenseEntries"
              entryType={EntryType.Expense}
              caseId={data?.case?.id || ''}
              caseUsers={data?.case?.caseToUsers || []}
              billingType={data?.case?.billing?.billingType}
              isFlatFeeHoursExhausted={data?.case?.billing?.isFlatFeeHoursExhausted}
            />

            <EntrySection
              key="investigatorEntries"
              title="Investigator Time"
              fieldName="investigatorEntries"
              entryType={EntryType.Cost}
              caseId={data?.case?.id || ''}
              caseUsers={data?.case?.caseToUsers || []}
              billingType={data?.case?.billing?.billingType}
              isFlatFeeHoursExhausted={data?.case?.billing?.isFlatFeeHoursExhausted}
            />

            <NetTotalSection netTotal={netTotal} litigation={data?.case} />

            <Box display="flex" justifyContent="flex-end" flexWrap="wrap" gap={2} mt={3}>
              <Button variant="outlined" onClick={onClose} disabled={generatingQuickBill}>
                Cancel
              </Button>
              <Button
                variant="outlined"
                type="submit"
                disabled={generatingQuickBill}
                onClick={() => setIsFinalBill(true)}
                endIcon={generatingQuickBill && isFinalBill && <CircularProgress size={16} color="inherit" />}
              >
                Generate Final-Bill
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={generatingQuickBill}
                onClick={() => setIsFinalBill(false)}
                endIcon={generatingQuickBill && !isFinalBill && <CircularProgress size={16} color="inherit" />}
              >
                Generate Pre-Bill
              </Button>
            </Box>
          </form>
        </FormProvider>
      </LocalizationProvider>
    </CustomModal>
  )
}

export default QuickBillModal
